import type { Pipeline, Project } from '@orgus/protocol';
import { worktreeLine, worktreeStates } from './worktrees.ts';
import type { WorktreeStates } from './worktrees.ts';
import { PIPELINE_STATE_LABEL, normalizeRemoteUrl } from '@orgus/protocol';
import { FolderGit2, Plus, Server } from '@orgus/ui/icons';
import { useMemo } from 'react';
import { useNavigate } from 'react-router';
import { useShallow } from 'zustand/react/shallow';
import { Button, EmptyState, IconButton, Panel, ProjectMark, Pressable, REVEAL_GROUP, RelativeTime, RevealRest, RevealSlot, StateBar, cn, plural } from '@orgus/ui';
import { useOrgus, useProjects } from '../../lib/store.ts';
import { openNewSession } from '../new-session/store.ts';
import { liveSessionCounts } from './counts.ts';
import { useNeedsYouState } from '../asks/needsYou.ts';
import { blockedOnly } from '../asks/levels.ts';

/** The pipeline a project's row reports: the one running, else the one in trouble, else the busiest. */
export function primaryPipeline(list: readonly Pipeline[]): Pipeline | undefined {
  const rank = (p: Pipeline) => (p.state === 'running' ? 0 : p.state === 'error' ? 1 : p.state === 'paused' ? 2 : 3);
  return [...list].sort((a, b) => rank(a) - rank(b) || b.items.length - a.items.length)[0];
}

interface ProjectLine {
  project: Project;
  checkouts: number;
  machines: number;
  remote: string | null;
  working: number;
  waiting: number;
  idle: number;
  pipeline: Pipeline | undefined;
  landedAt: number | null;
  worktrees: WorktreeStates;
}

function Row({ line, showQueue, compact }: { line: ProjectLine; showQueue: boolean; compact?: boolean }) {
  const navigate = useNavigate();
  const { project: p, pipeline } = line;
  const live = line.working + line.waiting + line.idle;
  const breakdown = [line.working && `${line.working} working`, line.waiting && `${line.waiting} waiting`, line.idle && `${line.idle} idle`].filter(Boolean).join(', ');
  const queue = pipeline ? (pipeline.state === 'running' || pipeline.state === 'idle' ? (pipeline.items.length ? `${pipeline.items.length} queued` : 'Queue empty') : PIPELINE_STATE_LABEL[pipeline.state]) : '';
  // The narrow Overview column: one labelled meta line (its sessions, then what its worktrees
  // hold that nobody is on), and one short status at the row's end, so every number says what it counts.
  const w = line.worktrees;
  const held = [w.toLand && `${w.toLand} to land`, w.uncommitted && `${w.uncommitted} uncommitted`].filter(Boolean);
  const compactMeta = live ? [plural(live, 'session'), ...held].join(', ') : held.length ? held.join(', ') : `${plural(line.checkouts, 'checkout')}, all clean`;
  return (
    <li className={cn(REVEAL_GROUP, 'motion-arrive relative flex min-h-row-2line items-center rounded-control transition-colors duration-(--duration-fast) hover:bg-hover')} data-row>
      <Pressable onClick={() => navigate(`/projects?project=${encodeURIComponent(p.id)}`)} className="flex min-h-row-2line min-w-0 flex-1 items-center gap-4 rounded-control px-3 py-1">
        <ProjectMark projectId={p.id} name={p.name} size="md" />
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="truncate text-ui font-medium text-fg">{p.name}</span>
          <span className="flex min-w-0 items-center gap-2 text-caption text-fg-subtle">
            {/* Worktrees by what they need: active, waiting to be landed, uncommitted with nobody on them. */}
            {/* On a phone the name keeps its line and the meta may take a second one, never cut. */}
            <span className={cn('min-w-0 truncate', compact && 'max-sm:line-clamp-2 max-sm:whitespace-normal')} data-meta title={`${plural(line.checkouts, 'checkout')}${line.machines > 1 ? ` on ${line.machines} machines` : ''}: ${worktreeLine(line.worktrees) || 'all clean'}${breakdown ? `. Sessions: ${breakdown}` : ''}`}>
              {compact ? compactMeta : worktreeLine(line.worktrees) || `${plural(line.checkouts, 'checkout')}, all clean`}
            </span>
            {line.remote && !compact && (
              <span className="hidden min-w-0 truncate md:inline" data-meta>
                {line.remote}
              </span>
            )}
          </span>
        </span>
        {/* In the narrow overview column the words carry it; the bar would only repeat "3 working". */}
        <StateBar
          className={compact ? 'hidden' : 'hidden w-24 shrink-0 lg:block'}
          counts={{ 'needs-you': line.waiting, running: line.working, queued: pipeline?.items.length ?? 0, stale: line.idle }}
        />
        {!compact && (
          <span className="flex min-w-0 shrink flex-col items-end text-caption num sm:w-48 sm:shrink-0" data-column="sessions" title={breakdown || undefined}>
            {line.waiting > 0 ? (
              <span className="font-medium text-status-attention">{line.waiting} {line.waiting === 1 ? 'needs' : 'need'} you</span>
            ) : line.working > 0 ? (
              <span className="text-fg-muted">{line.working} working</span>
            ) : live ? (
              <span className="text-fg-subtle">{live} idle</span>
            ) : (
              <span className="text-fg-subtle">No live sessions</span>
            )}
            {showQueue && (
              <span className={cn('hidden md:inline', pipeline?.state === 'error' ? 'text-status-danger' : 'text-fg-subtle')} data-column="queue">
                {queue}
                {line.landedAt ? (
                  <>
                    {queue ? ' · ' : ''}
                    <RelativeTime ts={line.landedAt} prefix="landed" />
                  </>
                ) : null}
              </span>
            )}
          </span>
        )}
        {/* Reserved for the hover action, so it never lands on the last column. In the narrow
            column the status itself is the trailing content, at the row's end. */}
        <RevealRest className={compact ? 'text-caption' : undefined} data-column={compact ? 'sessions' : undefined} title={compact ? breakdown || undefined : undefined}>
          {/* What differs between projects: who needs you, else how many work, else when it last landed. */}
          {compact &&
            (line.waiting > 0 ? (
              <span className="font-medium text-status-attention num">{line.waiting} {line.waiting === 1 ? 'needs' : 'need'} you</span>
            ) : line.working > 0 ? (
              <span className="text-fg-muted num">{line.working} working</span>
            ) : line.landedAt ? (
              <RelativeTime ts={line.landedAt} prefix="landed" className="text-fg-subtle num" />
            ) : live ? (
              <span className="text-fg-subtle">Idle</span>
            ) : null)}
        </RevealRest>
      </Pressable>
      <RevealSlot className="absolute right-3">
        <IconButton size="xs" label={`New session in ${p.name}`} icon={<Plus />} onClick={() => openNewSession({ projectId: p.id })} />
      </RevealSlot>
    </li>
  );
}

/** Every project on one line each: live sessions, its landing queue and when something last landed. */
/** compact: the narrow column on Overview (no remote, a short bar, no queue column). */
export function ProjectList({ compact, className }: { compact?: boolean; className?: string } = {}) {
  const all = useProjects();
  const navigate = useNavigate();
  const e = useOrgus(useShallow((s) => ({ sessions: s.sessions, checkouts: s.checkouts, pipelines: s.pipelines })));
  const machineCount = useOrgus((s) => Object.keys(s.machines).length);
  // Waiting means blocked in each project's counts (ADR 0056).
  const levels = useNeedsYouState().bySession;
  const lines = useMemo<ProjectLine[]>(() => {
    const sessions = Object.values(e.sessions);
    const checkouts = Object.values(e.checkouts);
    const pipelines = Object.values(e.pipelines);
    return all
      .map((p) => {
        const counts = liveSessionCounts(blockedOnly(sessions.filter((s) => s.projectId === p.id), levels));
        const cos = checkouts.filter((c) => c.projectId === p.id);
        const pipeline = primaryPipeline(pipelines.filter((x) => x.projectId === p.id));
        const landed = pipeline?.recent.find((i) => i.state === 'passed');
        const remote = p.remoteUrl ? normalizeRemoteUrl(p.remoteUrl).replace(/^[^/]+\//, '') : null;
        return {
          project: p,
          checkouts: cos.length,
          machines: new Set(cos.map((c) => c.machineId)).size,
          remote,
          working: counts.working,
          waiting: counts.waiting,
          idle: counts.idle,
          pipeline,
          landedAt: landed?.endedAt ?? null,
          worktrees: worktreeStates(cos, sessions.filter((s) => s.projectId === p.id)),
          rank: sessions.filter((s) => s.projectId === p.id).length,
        };
      })
      .sort((a, b) => b.waiting - a.waiting || b.working - a.working || b.rank - a.rank || a.project.name.localeCompare(b.project.name));
  }, [all, e, levels]);
  const showQueue = lines.some((l) => l.pipeline);

  return (
    <Panel icon={<FolderGit2 />} title="Projects" sub={lines.length || undefined} flush className={className}>
      {lines.length === 0 && machineCount === 0 ? (
        // First run: a project lives on a machine, so the way forward starts there.
        <EmptyState
          icon={<Server />}
          title="No machine yet"
          description="Connect a machine to see its repos here."
          action={
            <Button variant="primary" onClick={() => navigate('/getting-started?step=machine')}>
              Connect a machine
            </Button>
          }
        />
      ) : lines.length === 0 ? (
        <EmptyState
          icon={<FolderGit2 />}
          title="No projects yet"
          description="Connect a repo to see its pipeline, sessions and landings here."
          action={<Button onClick={() => navigate('/getting-started?step=repo')}>Connect a repo</Button>}
        />
      ) : (
        <ul className="flex flex-col px-1 pb-1" data-list aria-label="Projects">
          {lines.map((l) => (
            <Row key={l.project.id} line={l} showQueue={showQueue && !compact} compact={compact} />
          ))}
        </ul>
      )}
    </Panel>
  );
}
